/**
 * ESG Supplier Dashboard Component
 * Comprehensive supplier ecosystem management interface with ESG performance monitoring
 * Follows industry standards for supplier relationship management and ESG assessment
 */

import React, { useState, useEffect } from 'react';
import { 
  subscribe,
  getSupplierDashboardOverview,
  getSuppliers,
  getRiskMatrix,
  getPerformanceAnalytics,
  getCertificationTracking,
  getDiversityMetrics,
  getSustainabilityPrograms,
  getHighPerformingSuppliers,
  getRiskSuppliers,
  getCertificationExpiringSuppliers,
  getSupplierSpendAnalysis,
  generateSupplierReport,
  refreshSupplierData,
  exportSupplierData
} from '../../../services/esg/esgSupplierDashboardService';

const ESGSupplierDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [supplierData, setSupplierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribe((data) => {
      setSupplierData(data);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshSupplierData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = (format = 'json', filterCriteria = {}) => {
    const exportData = exportSupplierData(format, filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esg-supplier-dashboard-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>
            Loading ESG Supplier Dashboard...
          </p>
        </div>
      </div>
    );
  }

  const overview = getSupplierDashboardOverview();
  const suppliers = getSuppliers();
  const riskMatrix = getRiskMatrix();
  const performanceAnalytics = getPerformanceAnalytics();
  const certificationTracking = getCertificationTracking();
  const diversityMetrics = getDiversityMetrics();
  const sustainabilityPrograms = getSustainabilityPrograms();
  const spendAnalysis = getSupplierSpendAnalysis();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Pattern Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.1
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
              }}>
                🏭
              </div>
              <div>
                <h1 style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: 'white',
                  margin: '0 0 8px 0',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.5px'
                }}>
                  ESG Supplier Dashboard
                </h1>
                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.95)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Comprehensive supplier ecosystem management and ESG performance monitoring
                </p>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                padding: '12px 24px',
                background: refreshing ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                opacity: refreshing ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {refreshing ? '↻ Refreshing...' : '↻ Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '0', padding: '0 20px' }}>
          {[
            { id: 'overview', name: 'Overview', icon: '📊' },
            { id: 'suppliers', name: 'Suppliers', icon: '🏭' },
            { id: 'performance', name: 'Performance', icon: '📈' },
            { id: 'risk', name: 'Risk Management', icon: '⚠️' },
            { id: 'sustainability', name: 'Sustainability', icon: '🌱' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: '1',
                padding: '16px 20px',
                background: activeTab === tab.id ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #7c3aed' : '3px solid transparent',
                color: activeTab === tab.id ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '700' : '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab 
            overview={overview}
            spendAnalysis={spendAnalysis}
            performanceAnalytics={performanceAnalytics}
            diversityMetrics={diversityMetrics}
          />
        )}

        {activeTab === 'suppliers' && (
          <SuppliersTab 
            suppliers={suppliers}
            onSupplierSelect={setSelectedSupplier}
            selectedSupplier={selectedSupplier}
          />
        )}

        {activeTab === 'performance' && (
          <PerformanceTab 
            performanceAnalytics={performanceAnalytics}
            certificationTracking={certificationTracking}
            highPerformers={getHighPerformingSuppliers()}
          />
        )}

        {activeTab === 'risk' && (
          <RiskManagementTab 
            riskMatrix={riskMatrix}
            riskSuppliers={getRiskSuppliers()}
            expiringCertifications={getCertificationExpiringSuppliers()}
          />
        )}

        {activeTab === 'sustainability' && (
          <SustainabilityTab 
            sustainabilityPrograms={sustainabilityPrograms}
            diversityMetrics={diversityMetrics}
          />
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ overview, spendAnalysis, performanceAnalytics, diversityMetrics }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
    {/* Key Metrics */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px'
    }}>
      {/* Card 1: Total Suppliers */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(59, 130, 246, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}>
            🏭
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              Total Suppliers
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#60a5fa',
            textShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            lineHeight: '1'
          }}>
            {overview.totalSuppliers}
          </span>
          <span style={{
            fontSize: '16px',
            color: '#94a3b8',
            fontWeight: '600'
          }}>
            Suppliers
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#22c55e'
            }}>
              +3.2%
            </span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
          </div>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>
            {overview.activeSuppliers} active
          </span>
        </div>
      </div>

      {/* Card 2: Overall ESG Score */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 197, 94, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
          }}>
            🎯
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              Overall ESG Score
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#22c55e',
            textShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
            lineHeight: '1'
          }}>
            {overview.overallESGScore}
          </span>
          <span style={{
            fontSize: '16px',
            color: '#94a3b8',
            fontWeight: '600'
          }}>
            Score
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#22c55e'
            }}>
              +6.8%
            </span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
          </div>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>
            {overview.benchmarkPosition} industry
          </span>
        </div>
      </div>

      {/* Card 3: Total Spend */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(168, 85, 247, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(168, 85, 247, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)'
          }}>
            💰
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              Total Spend
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#a855f7',
            textShadow: '0 2px 8px rgba(168, 85, 247, 0.3)',
            lineHeight: '1'
          }}>
            {Math.round(overview.totalSpend / 1000000)}
          </span>
          <span style={{
            fontSize: '16px',
            color: '#94a3b8',
            fontWeight: '600'
          }}>
            M USD
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#22c55e'
            }}>
              +8.4%
            </span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
          </div>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>
            {overview.preferredSuppliers} preferred
          </span>
        </div>
      </div>

      {/* Card 4: Compliance Rate */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 197, 94, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
          }}>
            ✅
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              Compliance Rate
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#22c55e',
            textShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
            lineHeight: '1'
          }}>
            {overview.complianceRate}
          </span>
          <span style={{
            fontSize: '16px',
            color: '#94a3b8',
            fontWeight: '600'
          }}>
            %
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#22c55e'
            }}>
              +2.4%
            </span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
          </div>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>
            {overview.certifiedSuppliers} certified
          </span>
        </div>
      </div>
    </div>

    {/* ESG Performance Dashboard */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">ESG Performance Dashboard</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ESGScoreCard
          title="Environmental"
          score={overview.environmentalScore}
          trend={performanceAnalytics.categoryBreakdown.environmental.trend}
          target={performanceAnalytics.categoryBreakdown.environmental.target}
          color="green"
        />
        <ESGScoreCard
          title="Social"
          score={overview.socialScore}
          trend={performanceAnalytics.categoryBreakdown.social.trend}
          target={performanceAnalytics.categoryBreakdown.social.target}
          color="blue"
        />
        <ESGScoreCard
          title="Governance"
          score={overview.governanceScore}
          trend={performanceAnalytics.categoryBreakdown.governance.trend}
          target={performanceAnalytics.categoryBreakdown.governance.target}
          color="purple"
        />
      </div>
    </div>

    {/* Spend Analysis */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Spend by Tier</h3>
        <div className="space-y-3">
          {Object.entries(spendAnalysis.spendByTier).map(([tier, data]) => (
            <div key={tier} className="flex justify-between items-center">
              <span className="font-medium text-gray-700 capitalize">{tier}</span>
              <div className="text-right">
                <div className="font-bold text-gray-900">${(data.amount / 1000000).toFixed(1)}M</div>
                <div className="text-sm text-gray-500">{data.percentage}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Diversity Metrics</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Diverse Suppliers</span>
            <div className="text-right">
              <div className="font-bold text-green-600">{diversityMetrics.diverseSuppliers}</div>
              <div className="text-sm text-gray-500">{diversityMetrics.diversityPercentage}%</div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Women-Owned</span>
            <div className="text-right">
              <div className="font-bold text-blue-600">{diversityMetrics.categories.womenOwned.count}</div>
              <div className="text-sm text-gray-500">{diversityMetrics.categories.womenOwned.percentage}%</div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Minority-Owned</span>
            <div className="text-right">
              <div className="font-bold text-purple-600">{diversityMetrics.categories.minorityOwned.count}</div>
              <div className="text-sm text-gray-500">{diversityMetrics.categories.minorityOwned.percentage}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Risk Summary */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <RiskCard title="High Risk" count={overview.riskSummary.highRisks} color="red" />
        <RiskCard title="Medium Risk" count={overview.riskSummary.mediumRisks} color="yellow" />
        <RiskCard title="Low Risk" count={overview.riskSummary.lowRisks} color="green" />
        <RiskCard title="Total Risks" count={overview.riskSummary.totalRisks} color="gray" />
      </div>
    </div>
  </div>
);

// Suppliers Tab Component
const SuppliersTab = ({ suppliers, onSupplierSelect, selectedSupplier }) => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');

  const filteredSuppliers = suppliers.filter(supplier => {
    const categoryMatch = filterCategory === 'all' || supplier.category === filterCategory;
    const riskMatch = filterRisk === 'all' || supplier.riskLevel === filterRisk;
    return categoryMatch && riskMatch;
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="Primary Manufacturing">Primary Manufacturing</option>
              <option value="Logistics & Transportation">Logistics & Transportation</option>
              <option value="Raw Materials">Raw Materials</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Risk Levels</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid gap-6">
        {filteredSuppliers.map((supplier) => (
          <SupplierCard
            key={supplier.id}
            supplier={supplier}
            isSelected={selectedSupplier?.id === supplier.id}
            onSelect={onSupplierSelect}
          />
        ))}
      </div>
    </div>
  );
};

// Performance Tab Component
const PerformanceTab = ({ performanceAnalytics, certificationTracking, highPerformers }) => (
  <div className="space-y-6">
    {/* Performance Trends */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Trends</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(performanceAnalytics.trends).map(([key, trend]) => (
          <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">
              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </div>
            <div className={`text-2xl font-bold ${trend.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
              {trend}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Certification Status */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Certification Management</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <CertificationCard
          title="Total Certifications"
          value={certificationTracking.totalCertifications}
          subtitle="All certifications"
          color="blue"
        />
        <CertificationCard
          title="Valid Certifications"
          value={certificationTracking.validCertifications}
          subtitle={`${((certificationTracking.validCertifications / certificationTracking.totalCertifications) * 100).toFixed(1)}% valid`}
          color="green"
        />
        <CertificationCard
          title="Expiring Soon"
          value={certificationTracking.expiringIn30Days + certificationTracking.expiringIn90Days}
          subtitle="Next 90 days"
          color="yellow"
        />
        <CertificationCard
          title="Average Score"
          value={certificationTracking.averageScore}
          subtitle="Quality score"
          color="purple"
        />
      </div>
    </div>

    {/* Top Performers */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Performing Suppliers</h2>
      <div className="space-y-3">
        {highPerformers.slice(0, 5).map((supplier, index) => (
          <div key={supplier.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">{index + 1}</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{supplier.name}</h3>
                <p className="text-sm text-gray-500">{supplier.category} • {supplier.location}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="font-bold text-green-600">{supplier.esgScores.overall.toFixed(1)}</div>
              <div className="text-sm text-gray-500">ESG Score</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Risk Management Tab Component
const RiskManagementTab = ({ riskMatrix, riskSuppliers, expiringCertifications }) => (
  <div className="space-y-6">
    {/* Risk Matrix */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Risk Assessment Matrix</h2>
      <div className="grid gap-4">
        {riskMatrix.categories.map((category, index) => (
          <div key={index} className="p-4 border rounded-lg">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-medium text-gray-900">{category.name}</h3>
                <p className="text-sm text-gray-600">{category.subcategories.join(', ')}</p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  category.overallRisk === 'Low' ? 'bg-green-100 text-green-800' :
                  category.overallRisk === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {category.overallRisk} Risk
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Suppliers Affected: </span>
                <span className="font-medium">{category.suppliersAffected}</span>
              </div>
              <div>
                <span className="text-gray-600">Trend: </span>
                <span className="font-medium">{category.trend}</span>
              </div>
              <div>
                <span className="text-gray-600">Mitigation: </span>
                <span className="font-medium text-green-600">{category.mitigationProgress.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Risk Heat Map */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Risk Heat Map</h2>
      <div className="grid gap-3">
        {riskMatrix.heatMap.map((risk, index) => (
          <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">{risk.category}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Likelihood: {risk.likelihood}</span>
                <span>Impact: {risk.impact}</span>
                <span>Suppliers: {risk.suppliers}</span>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-lg font-bold ${
                risk.riskScore >= 7.0 ? 'text-red-600' :
                risk.riskScore >= 4.0 ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {risk.riskScore.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500">Risk Score</div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* At-Risk Suppliers */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Suppliers Requiring Attention</h2>
      <div className="space-y-3">
        {riskSuppliers.slice(0, 5).map((supplier) => (
          <div key={supplier.id} className="p-4 border-l-4 border-red-400 bg-red-50 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-gray-900">{supplier.name}</h3>
                <p className="text-sm text-gray-600">{supplier.category} • {supplier.location}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-red-600">ESG Score: {supplier.esgScores.overall.toFixed(1)}</span>
                  <span className="text-yellow-600">Risk: {supplier.riskLevel}</span>
                </div>
              </div>
              <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                Review
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Sustainability Tab Component
const SustainabilityTab = ({ sustainabilityPrograms, diversityMetrics }) => (
  <div className="space-y-6">
    {/* Sustainability Programs */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Sustainability Programs</h2>
      <div className="space-y-6">
        {sustainabilityPrograms.map((program, index) => (
          <div key={index} className="border rounded-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{program.name}</h3>
                <p className="text-gray-600 mt-1">{program.description}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">{program.progressScore.toFixed(1)}%</div>
                <div className="text-sm text-gray-500">Progress</div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900">{program.participants}</div>
                <div className="text-sm text-gray-600">Participants</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-green-600">${(program.totalInvestment / 1000000).toFixed(1)}M</div>
                <div className="text-sm text-gray-600">Investment</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-blue-600">{program.milestones.filter(m => m.status === 'Complete').length}</div>
                <div className="text-sm text-gray-600">Milestones</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(program.metrics).map(([key, metric]) => (
                <div key={key} className="p-3 border rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{key.replace(/([A-Z])/g, ' $1')}</div>
                  <div className="font-semibold text-gray-900">{metric.value} {metric.unit}</div>
                  <div className="text-xs text-gray-500">Target: {metric.target}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Diversity Progress */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Supplier Diversity Progress</h2>
      <div className="space-y-4">
        {Object.entries(diversityMetrics.targets).map(([category, target]) => (
          <div key={category} className="p-4 border rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium text-gray-900 capitalize">{category.replace('Owned', ' Owned')}</h3>
              <span className="text-sm text-gray-500">{target.progress.toFixed(1)}% of target</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, target.progress)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600 mt-1">
              <span>Current: {target.current}%</span>
              <span>Target: {target.target}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Utility Components
const MetricCard = ({ title, value, unit, trend, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    red: 'bg-red-50 border-red-200'
  };

  const trendColor = trend.startsWith('+') ? 'text-green-600' : trend.startsWith('-') ? 'text-red-600' : 'text-gray-600';

  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-sm font-medium ${trendColor}`}>{trend}</span>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <div className="flex items-baseline">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        <span className="text-sm text-gray-500 ml-1">{unit}</span>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
};

const ESGScoreCard = ({ title, score, trend, target, color }) => {
  const colorClasses = {
    green: 'border-green-200 bg-green-50',
    blue: 'border-blue-200 bg-blue-50',
    purple: 'border-purple-200 bg-purple-50'
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${colorClasses[color]}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-2xl font-bold text-gray-900">{score.toFixed(1)}</span>
          <span className="text-sm text-gray-500 ml-1">Score</span>
        </div>
        <div className="text-right">
          <div className="text-sm text-green-600 font-medium">{trend}</div>
          <div className="text-xs text-gray-500">Target: {target}</div>
        </div>
      </div>
    </div>
  );
};

const RiskCard = ({ title, count, color }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    green: 'bg-green-100 text-green-800',
    gray: 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="text-center p-4 border rounded-lg">
      <div className={`text-2xl font-bold ${colorClasses[color].replace('bg-', 'text-').replace('100', '600')}`}>
        {count}
      </div>
      <div className="text-sm text-gray-600 mt-1">{title}</div>
    </div>
  );
};

const CertificationCard = ({ title, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</div>
      <div className="text-sm font-medium text-gray-900">{title}</div>
      <div className="text-xs text-gray-500">{subtitle}</div>
    </div>
  );
};

const SupplierCard = ({ supplier, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  
  const riskColors = {
    Low: 'text-green-600 bg-green-100',
    Medium: 'text-yellow-600 bg-yellow-100',
    High: 'text-red-600 bg-red-100'
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border ${isSelected ? 'border-blue-500' : 'border-gray-200'}`}>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{supplier.name}</h3>
              <span className="text-sm text-gray-500">|</span>
              <span className="text-sm text-gray-600">{supplier.category}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[supplier.riskLevel]}`}>
                {supplier.riskLevel} Risk
              </span>
            </div>
            <p className="text-gray-600 mb-2">{supplier.location} • {supplier.tier} • {supplier.relationship}</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">ESG Score:</span>
                <span className="font-semibold text-green-600">{supplier.esgScores.overall.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Spend:</span>
                <span className="font-semibold text-gray-900">${(supplier.spend / 1000000).toFixed(1)}M</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <span className="font-semibold text-blue-600">{supplier.status}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSelect(supplier)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              View Details
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-2 p-2 text-gray-400 hover:text-gray-600"
            >
              {expanded ? '−' : '+'}
            </button>
          </div>
        </div>

        {/* Quick Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Quality</div>
            <div className="font-semibold text-gray-900">{supplier.performance.qualityScore.toFixed(1)}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Delivery</div>
            <div className="font-semibold text-gray-900">{supplier.performance.deliveryPerformance.toFixed(1)}%</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Sustainability</div>
            <div className="font-semibold text-green-600">{supplier.performance.sustainability.toFixed(1)}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Certifications</div>
            <div className="font-semibold text-blue-600">{supplier.certifications.length}</div>
          </div>
        </div>

        {expanded && (
          <div className="space-y-4 border-t pt-4">
            {/* ESG Breakdown */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">ESG Breakdown</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(supplier.esgScores).map(([key, score]) => (
                  <div key={key} className="text-center p-2 border rounded">
                    <div className="text-xs text-gray-600 mb-1 capitalize">{key}</div>
                    <div className="font-semibold text-gray-900">{score.toFixed(1)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Certifications */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Active Certifications</h4>
              <div className="flex flex-wrap gap-2">
                {supplier.certifications.slice(0, 5).map((cert, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {cert.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Primary Contact: </span>
                  <span className="font-medium">{supplier.contactInfo.primaryContact}</span>
                </div>
                <div>
                  <span className="text-gray-600">ESG Contact: </span>
                  <span className="font-medium">{supplier.contactInfo.esgContact}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ESGSupplierDashboard;